if (!customElements.get('resources-grid')) {
  class ResourcesGrid extends HTMLElement {
    constructor() {
      super();
      this.enableInfiniteScroll = this.hasAttribute('infinite-scroll');
      this.enableInfiniteLoad = this.hasAttribute('infinite-load');
      this.infiniteLoadURL = this.getAttribute('infinite-load') || null;
    }

    connectedCallback() {
      const cards = Array.from(this.querySelectorAll('.masonry-item'));
      this.sortCards(cards);
      cards.forEach(card => {
        this.appendChild(card);
      });
      const mediaQuery = window.matchMedia('(max-width: 989px)');
      
      this.randomlyEnlargeCards(cards);

      this.masonry = new Masonry( this, {
        columnWidth: '.masonry-grid-sizer',
        itemSelector: '.masonry-item',
        percentPosition: true,
      });

      // Apply a random offset to each item for visual variety ranging from -40px to +40px in both directions
      this.masonry.on('layoutComplete', () => {
        if (mediaQuery.matches) return; // Skip on mobile
        this.querySelectorAll('.masonry-item').forEach(card => {
          const offsetX = Math.floor(Math.random() * 31) - 15;
          const offsetY = Math.floor(Math.random() * 31) - 15;
          card.querySelector('.resource-card, .resource-content-block').style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        });
        this.classList.add('masonry-layout-complete');
      });

      this.masonry.layout();

      // Remove translate offsets on switching to mobile to maintain layout integrity
      const handleMediaChange = (e) => {
        if (e.matches) {
          this.querySelectorAll('.masonry-item').forEach(card => {
            card.querySelector('.resource-card, .resource-content-block').style.transform = '';
          });
          this.classList.remove('masonry-layout-complete');
        } else {
          this.masonry.layout();
        }
      };
      mediaQuery.addEventListener('change', handleMediaChange);

      // Add intersection observer to observe every card for fade-in effect
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            this.observer.unobserve(entry.target); // Stop observing once visible
          }
        });
      }, { threshold: 0.1 });
      this.querySelectorAll('.masonry-item').forEach(card => {
        this.observer.observe(card);
      });
      
      // Call infiniteLoad() when scrolled to bottom
      if (this.enableInfiniteScroll && !this.enableInfiniteLoad) {
        window.addEventListener('scroll', () => {
          if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 400) {
            this.infiniteLoad(cards);
          }
        });
      }

      if (this.enableInfiniteLoad) {
        window.addEventListener('scroll', () => {
          if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 400) {
            this.loadNextPage();
          }
        });
      }
    }
    
    /**
     * Loads the next page of data from the specified infinite load URL.
     * If the URL is not set, the function will return early.
     * This function prevents multiple simultaneous loads by using an AbortController.
     *
     * @returns {void} This function does not return a value.
     */
    loadNextPage() {
      if (!this.infiniteLoadURL) return;
      // Prevent multiple simultaneous loads with abortcontroller
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      fetch(this.infiniteLoadURL, { signal })
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(data => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');
          const nextURL = doc.querySelector('resources-grid').getAttribute('infinite-load');
          this.infiniteLoadURL = nextURL;

          if (!this.infiniteLoadURL) {
            // No more pages to load, remove the scroll listener
            window.removeEventListener('scroll', this.loadNextPage);

            this.enableInfiniteLoad = false;
          }

          const newCards = doc.querySelectorAll('.masonry-item');
          newCards.forEach(card => {
            card.classList.remove('visible');
            this.appendChild(card);
            this.observer.observe(card);
            this.masonry.appended(card);
          });
          // this.masonry.layout();
        })
        .catch(error => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted');
          } else {
            console.error('Fetch error:', error);
          }
        });
    }

    /**
     * Observes all cards within the grid for visibility using Intersection Observer.
     * 
     * @returns {void}
     */
    observeCards() {
      const cards = this.querySelectorAll('.masonry-item');
      cards.forEach(card => {
        this.observer.observe(card);
      });
    }

    /**
     * Sorts an array of card elements based on their class.
     * Content blocks (elements with the class 'resource-content-block') are biased to appear earlier in the sorted array.
     * The sorting is done randomly with a bias that can be tuned.
     *
     * @param {HTMLElement[]} cards - An array of card elements to be sorted.
     * @returns {void} This function modifies the input array in place.
     */
    sortCards(cards) {
      cards.sort((a, b) => {
        const aIsContent = a.classList.contains('resource-content-block');
        const bIsContent = b.classList.contains('resource-content-block');
      
        // Base random value
        let weight = Math.random() - 0.5;
      
        // Bias content blocks earlier (tune 0.3–0.7)
        if (aIsContent && !bIsContent) weight -= 0.7;
        if (!aIsContent && bIsContent) weight += 0.7;
      
        return weight;
      });
    }

    /**
     * Randomly enlarges a subset of resource cards.
     * Each card has a 15% chance of being enlarged by adding a specific class.
     *
     * @param {HTMLElement[]} cards - An array of card elements to potentially enlarge.
     * @returns {void}
     */
    randomlyEnlargeCards(cards) {
      cards.forEach(card => {
        if (Math.random() < 0.15) { // 15% chance to enlarge
          card.classList.add('masonry-item--width-2');
        }
      });
    }

    /**
     * Initializes a custom grid layout for resource cards and content blocks.
     * This method sorts the cards, calculates their positions to avoid overlap,
     * and sets the container's height based on the positioned cards.
     *
     * @this {HTMLElement} The element that contains the resource cards and content blocks.
     * @returns {void} This method does not return a value.
     * 
     * @deprecated Use Masonry layout instead.
     */
    initCustomGrid() {
      const cards = Array.from(this.querySelectorAll('.resource-card, .resource-content-block'));
      this.sortCards(cards);

      this.style.position = "relative";

      const containerWidth = this.clientWidth;
      const padding = 80;

      // Estimated minimum spacing for cards
      const minX = padding;
      const maxX = containerWidth - padding;

      const placed = []; // store placed card rects

      cards.forEach(card => {
        card.style.position = "absolute";

        const w = card.offsetWidth;
        const h = card.offsetHeight;

        // Random horizontal position (centered within container)
        let x = Math.random() * (maxX - minX - w) + minX;

        // Start high, push downward on collision
        let y = padding;

        let placeRect = { x, y, w, h };
        let foundSpot = false;
        let safety = 0;

        while (!foundSpot && safety < 5000) {
          foundSpot = true;

          for (const other of placed) {
            if (this.rectsOverlap(placeRect, other)) {
              // Move card down just below the collision
              placeRect.y = other.y + other.h + 20; // 20px vertical spacing
              foundSpot = false;
            }
          }

          safety++;
        }

        // Add card position
        placed.push(placeRect);

        // Apply final style
        card.style.left = placeRect.x + "px";
        card.style.top = placeRect.y + "px";
      });

      // After all cards placed → set container height
      const bottomMost = Math.max(...placed.map(p => p.y + p.h));
      this.style.height = (bottomMost + padding) + "px";
    }

    /**
     * Checks if two rectangles overlap.
     *
     * @param {Object} a - The first rectangle.
     * @param {number} a.x - The x-coordinate of the first rectangle's top-left corner.
     * @param {number} a.y - The y-coordinate of the first rectangle's top-left corner.
     * @param {number} a.w - The width of the first rectangle.
     * @param {number} a.h - The height of the first rectangle.
     * @param {Object} b - The second rectangle.
     * @param {number} b.x - The x-coordinate of the second rectangle's top-left corner.
     * @param {number} b.y - The y-coordinate of the second rectangle's top-left corner.
     * @param {number} b.w - The width of the second rectangle.
     * @param {number} b.h - The height of the second rectangle.
     * @returns {boolean} True if the rectangles overlap, false otherwise.
     * 
     * @deprecated Used in the deprecated initCustomGrid method.
     */
    rectsOverlap(a, b) {
      return !(
        a.x + a.w <= b.x ||
        a.x >= b.x + b.w ||
        a.y + a.h <= b.y ||
        a.y >= b.y + b.h
      );
    }

    /**
     * Take copies of the first elements and move them to the end to create an infinite scroll effect.
     */
    infiniteLoad(cards) {
      const firstElements = Array.from(cards.sort(() => 0.5 - Math.random()).slice(0, 15));
      firstElements.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.remove('visible');
        this.appendChild(clone);
        this.observer.observe(clone);
        this.masonry.appended(clone);
      });
    }
  }
  customElements.define('resources-grid', ResourcesGrid);
}